import { useApplicationContext } from "src/hooks/useApplication";
import { useEntityContext } from "../../useEntityContext";
import { EntityTypes } from "@vzbl/integrations";
import { Card, Col, Row, Typography, Tooltip, Empty } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { HeaderPanel } from "./layout/HeaderPanel";
import { toHeaderAttributes } from "./HeaderAttributes";
import { SummaryPanel } from "./layout/SummaryPanel";
import { StandardCard } from "./StandardCard";
import { theme } from "antd";
import { MetricsDisplay } from "./MetricsDisplay";
import {
  StageProgress,
  StatusHistoryItem,
  generateStatusHistoryFromEvents,
  normalizeEvents,
} from "src/components/progress";
import { useEntitiesList } from "src/search/lib/useEntitiesList";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { useEntityHistory } from "../../useEntityHistory";
import { useOrg } from "src/hooks/useOrg";
import { EntityTimeline } from "src/components/progress/EntityTimeline";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { EntityContributors } from "./EntityContributors";
import { DynamicProperties } from "./DynamicProperties";
import { Contributors } from "../../Contributors";
import { useEffect, useMemo } from "react";
import {
  pullRequestDisplayRules,
  hiddenPullRequestFields,
  formatExplanation,
} from "./EntityDisplayRules";

const { Title } = Typography;

interface PullRequestOverviewProps {
  entity: any;
  statusHistory?: StatusHistoryItem[];
  entityHistory?: any;
}

export const PullRequestOverview: React.FC<PullRequestOverviewProps> = ({
  entity,
  statusHistory = [],
}) => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const { uuid } = entity.properties;

  const entityHistory = useEntityHistory({
    orgId: useOrg().id!,
    entityUuid: uuid,
    type: EntityTypes.PullRequest,
  });

  const entityStatusHistory = useMemo(() => {
    if (entityHistory.query.isLoading || !entityHistory.query.data?.data) {
      return [];
    }
    const events = normalizeEvents(entityHistory.query.data.data);
    return generateStatusHistoryFromEvents(events, entity);
  }, [entityHistory.query.data, entity]);

  const searchState = useMemo(
    () => ({
      orgId: orgId,
      filters: entity
        ? [
            {
              key: "Event.PullRequest.uuid",
              target: "PullRequest",
              attribute: "uuid",
              comparison: { IN: [uuid] },
            },
          ]
        : [],
      select: [EntityTypes.Event],
      timezone: "America/New_York",
      sort: [{ target: "Event", attribute: "occurredAt", direction: "DESC" }],
      pageSize: 1000,
    }),
    [orgId, entity]
  );

  const serialized = useMemo(() => serializeParams(searchState), [searchState]);
  const activities = useEntitiesList({ state: searchState, serialized });

  useEffect(() => {
    activities.subscribe();
    return () => activities.unsubscribe();
  }, [activities]);

  const normalizedEvents = normalizeEvents(activities.data?.data ?? []);

  return (
    <div>
      <Content
        entity={entity}
        statusHistory={entityStatusHistory}
        entityHistory={{
          isLoading: activities.isLoading,
          data: normalizedEvents,
          error: activities.error,
        }}
      />
    </div>
  );
};

const Content: React.FC<PullRequestOverviewProps> = ({
  entity,
  statusHistory = [],
  entityHistory,
}) => {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const { token } = theme.useToken();

  const {
    prNumber,
    status,
    integration,
    title,
    description: body,
    vzblSummary,
    merged,
    baseBranch,
    commits,
    number,
    branch,
    state,
    base,
    vzblSummaryPurpose,
  } = entity.properties;

  const handlePropertyClick = (key: string, value: any) => {
    console.log("Property clicked:", { key, value });
  };

  return (
    <div style={{ padding: "24px", paddingBottom: "48px" }}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <HeaderPanel
            entity={entity}
            attributes={toHeaderAttributes({
              orgId,
              context,
              entityType: EntityTypes.PullRequest,
              attributes: [
                { name: "base", text: base, type: "metadata" },
                { name: "status", text: status, type: "status" },
                { name: "state", text: state, type: "status" },
              ],
            })}
            icon={<IntegrationIcon source={integration} size="large" />}
            label={title}
          />
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        style={{ height: "550px", overflow: "hidden", marginBottom: "24px" }}
      >
        <Col span={16}>
          <SummaryPanel
            title={title}
            description={body}
            aiSummary={vzblSummaryPurpose}
          />
        </Col>
        <Col span={8}>
          <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
            Properties
          </Title>
          <StandardCard
            style={{ height: "500px", padding: "0px 12px" }}
            headerStyle={{ display: "none" }}
          >
            <DynamicProperties
              properties={entity.properties}
              orgId={orgId}
              context={context}
              entityType={EntityTypes.PullRequest}
              maxInitialProperties={9}
              showGroupHeaders={true}
              showMoreButton={true}
              layout="split"
              displayRules={pullRequestDisplayRules}
              hiddenFields={hiddenPullRequestFields}
              onPropertyClick={handlePropertyClick}
            />
          </StandardCard>
        </Col>
      </Row>

      <Row
        gutter={[24, 24]}
        style={{
          height: "600px",
          marginBottom: "48px",
          marginLeft: "0px",
          marginRight: "0px",
        }}
      >
        <Col span={12}>
          <div style={{ marginBottom: 24 }}>
            <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
              Progress
            </Title>
            <StandardCard
              title="Progress"
              headerStyle={{ display: "none" }}
              bodyStyle={{ padding: "12px" }}
              style={{ height: "100px", marginBottom: 24, flexShrink: 0 }}
              variant="grey"
            >
              <StageProgress
                currentStatus={status}
                statusHistory={statusHistory}
                showTimestamps
                showDuration
                style={{ minHeight: "0" }}
                template="PR_WORKFLOW"
              />
            </StandardCard>

            <StandardCard
              title="Timeline"
              headerStyle={{ display: "none" }}
              bodyStyle={{ padding: "12px" }}
              style={{ height: "100%" }}
            >
              {entityHistory.isLoading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <LoadingOutlined />
                </div>
              ) : entityHistory.error ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  Failed to load timeline
                </div>
              ) : entityHistory.data?.length ? (
                <EntityTimeline
                  history={entityHistory.data}
                  style={{
                    minHeight: "100%",
                    overflowY: "auto",
                    padding: "0px 12px 12px 0px",
                  }}
                />
              ) : (
                <Empty description="No history available" />
              )}
            </StandardCard>
          </div>
        </Col>

        <Col span={12}>
          <div style={{ marginBottom: "24px" }}>
            <div style={{ marginBottom: 24, flexShrink: 0 }}>
              <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
                Metrics
              </Title>
              <MetricsDisplay
                entity={entity}
                maxMetrics={3}
                config={{
                  hiddenMetrics: [
                    "vzblClarityScore",
                    "vzblFrictionScore",
                    "vzblClassification",
                  ],
                }}
                style={{ height: "100px" }}
              />
            </div>
            <Contributors>
              <EntityContributors
                entity={entity}
                showTitle={false}
                style={{
                  height: "100%",
                  padding: "12px",
                  marginBottom: "24px",
                }}
              />
            </Contributors>
          </div>
        </Col>
      </Row>
    </div>
  );
};
